import nationalities from 'data/nationalities.json'
import { getPersonRules, getPersonSchema } from 'src/services/PersonApiStore'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import type { PropertyEvaluatorClass } from 'src/services/mappings/PropertyEvaluatorClass/PropertyEvaluatorClass'
import { getDependenciesFieldRules } from 'src/services/mappings/jsonSchema/jsonSchema.utils'
import {
  ADULT_MIN_AGE,
  ALTERNATE_RESIDENCE_LEGAL_CAPACITIES,
  NO_ALTERNATE_RESIDENCE_MIN_AGE,
} from 'src/services/mappings/person/person.const'
import type {
  DynamicEnumsRuleInterface,
  FieldInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { FIELD_RULES } from 'src/tpbp-core/models/fields/fieldRules.const'
import type { PersonModel } from 'src/tpbp-json-api-handler/models/models'
import { logger } from 'src/utils/logger.utils'

function getPersonProperty(
  record: ProxyRecord,
  originKey: string,
): PropertyEvaluatorClass | false | null {
  const rules = getPersonRules(record)
  if (!rules) {
    logger.warn('No rule found')
    return false
  }

  return rules[originKey] || null
}

function getFieldStatus(record, originKey, fieldStatusesByOriginKey) {
  const rule = getPersonProperty(record, originKey)
  if (!rule) {
    return false
  }

  return rule.getFieldStatus(record, fieldStatusesByOriginKey)
}

export function personIsOptional(record, originKey, fieldStatusesByOriginKey) {
  const status = getFieldStatus(record, originKey, fieldStatusesByOriginKey)

  return status === FIELD_RULES.isOptional
}

export function personIsMandatory(record, originKey, fieldStatusesByOriginKey) {
  const status = getFieldStatus(record, originKey, fieldStatusesByOriginKey)

  return status === FIELD_RULES.isMandatory
}

// TODO mutualise personIsMandatory, personIsOptional and personIsReadOnly
// in order to call it only once (better performances) and simplify code
export function personIsReadOnly(record, originKey, fieldStatusesByOriginKey) {
  const status = getFieldStatus(record, originKey, fieldStatusesByOriginKey)

  return status === FIELD_RULES.isReadOnly
}

function getPersonNationalitiesEnums() {
  return nationalities
}

export function getPersonEnums(
  record: PersonModel,
  field: FieldInterface,
): FieldInterface['enumKeys'] {
  if (field.key === 'nationalities' || field.key === 'fiscalResidence') {
    return getPersonNationalitiesEnums()
  }

  const schema = getPersonSchema(record)

  const fieldRules = schema
    ? getDependenciesFieldRules(schema.dependencies, record)
    : null

  const fieldRule = fieldRules?.[field.originKey]

  if (fieldRule?.enum) return fieldRule.enum

  const properties = schema?.properties
  const property = properties?.[field.originKey]

  if (property?.enum) return property.enum

  if (field.enumKeys) return field.enumKeys

  throw new Error(`Unknown person mapping case '${field.key}'`)
}

export const alternatedResidenceIsHidden = (record) =>
  !ALTERNATE_RESIDENCE_LEGAL_CAPACITIES.includes(record.legalCapacity) ||
  record.age >= NO_ALTERNATE_RESIDENCE_MIN_AGE

export const legalCapacityDynamicEnumRules: DynamicEnumsRuleInterface[] = [
  {
    expression: (record) => ((record?.age as number) ?? 0) < ADULT_MIN_AGE,
    getEnumKeys: (record, { getAdditionalRules }) => {
      const personAdditionalRules = getAdditionalRules(record)

      return personAdditionalRules?.legal_capacity?.minor_legal_capacities || []
    },
  },
  {
    expression: (record) => ((record?.age as number) ?? 0) >= ADULT_MIN_AGE,
    getEnumKeys: (record, { getAdditionalRules }) => {
      const personAdditionalRules = getAdditionalRules(record)

      return personAdditionalRules?.legal_capacity?.adult_legal_capacities || []
    },
  },
]
